﻿<template>
  <ContentWrap>
    <!-- 查询区域 -->
    <el-form
      ref="queryFormRef"
      :inline="true"
      :model="queryParams"
      class="-mb-15px"
      label-width="68px"
    >
      <el-form-item label="店铺" prop="shopId">
        <el-select
          v-model="queryParams.shopId"
          class="!w-240px"
          clearable
          placeholder="请选择店铺"
          filterable
          @change="handleQuery"
        >
          <el-option v-for="item in shopList" :key="item.id" :label="item.name" :value="item.id" />
        </el-select>
      </el-form-item>
      <el-form-item label="员工" prop="employeeId">
        <el-select
          v-model="queryParams.employeeId"
          class="!w-240px"
          clearable
          placeholder="请选择员工"
          filterable
        >
          <el-option
            v-for="item in employeeList"
            :key="item.id"
            :label="item.name + ' (' + (item.employeeNo || item.id) + ')'"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="考核周期" prop="period">
        <el-date-picker
          v-model="queryParams.period"
          class="!w-240px"
          clearable
          placeholder="请选择考核周期"
          type="month"
          value-format="YYYY-MM"
        />
      </el-form-item>
      <el-form-item label="等级" prop="grade">
        <el-select
          v-model="queryParams.grade"
          class="!w-240px"
          clearable
          placeholder="请选择等级"
        >
          <el-option label="A (优秀, >=90分)" value="A" />
          <el-option label="B (良好, >=80分)" value="B" />
          <el-option label="C (合格, >=70分)" value="C" />
          <el-option label="D (待改进, >=60分)" value="D" />
          <el-option label="E (不合格, <60分)" value="E" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置
        </el-button>
        <el-button
          v-hasPermi="['hotel:performance:create']"
          plain
          type="primary"
          @click="openForm('create')"
        >
          <Icon class="mr-5px" icon="ep:plus" />
          新增考核
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <!-- 表格区域 -->
  <ContentWrap>
    <el-table v-loading="loading" :data="list">
      <el-table-column align="center" label="员工姓名" prop="employeeName" min-width="100">
        <template #default="scope">
          <span>{{ scope.row.employeeName || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column align="center" label="岗位" prop="positionName" min-width="100">
        <template #default="scope">
          <span>{{ scope.row.positionName || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column align="center" label="考核周期" prop="period" min-width="110" />
      <el-table-column align="center" label="得分" prop="score" min-width="80">
        <template #default="scope">
          <span :style="{ color: getScoreColor(scope.row.score), fontWeight: 'bold' }">
            {{ scope.row.score != null ? scope.row.score : '-' }}
          </span>
        </template>
      </el-table-column>
      <el-table-column align="center" label="等级" prop="grade" min-width="80">
        <template #default="scope">
          <el-tag v-if="scope.row.grade === 'A'" type="success" size="default">A</el-tag>
          <el-tag v-else-if="scope.row.grade === 'B'" type="" size="default">B</el-tag>
          <el-tag v-else-if="scope.row.grade === 'C'" type="warning" size="default">C</el-tag>
          <el-tag v-else-if="scope.row.grade === 'D'" type="danger" size="default">D</el-tag>
          <el-tag v-else-if="scope.row.grade === 'E'" type="info" size="default">E</el-tag>
          <span v-else class="text-gray-400">-</span>
        </template>
      </el-table-column>
      <el-table-column align="center" label="各项指标" min-width="200">
        <template #default="scope">
          <div v-if="getIndicators(scope.row.metrics).length > 0" class="indicator-tags">
            <el-tag
              v-for="(ind, idx) in getIndicators(scope.row.metrics)"
              :key="idx"
              class="mr-5px mb-5px"
              size="small"
              type=""
            >
              {{ ind.name }}: {{ ind.score }}/{{ ind.maxScore || 100 }}
            </el-tag>
          </div>
          <span v-else class="text-gray-400">-</span>
        </template>
      </el-table-column>
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="创建时间"
        prop="createTime"
        width="180"
      />
      <el-table-column align="center" label="操作" width="210" fixed="right">
        <template #default="scope">
          <el-button
            v-hasPermi="['hotel:performance:query']"
            link
            type="primary"
            @click="openDetail(scope.row)"
          >
            详情
          </el-button>
          <el-button
            v-hasPermi="['hotel:performance:update']"
            link
            type="primary"
            @click="openForm('update', scope.row.id)"
          >
            修改
          </el-button>
          <el-button
            v-hasPermi="['hotel:performance:delete']"
            link
            type="danger"
            @click="handleDelete(scope.row.id)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <!-- 新建/编辑弹窗 -->
  <Dialog v-model="dialogVisible" :title="dialogTitle" width="750px">
    <el-form
      ref="formRef"
      v-loading="formLoading"
      :model="formData"
      :rules="formRules"
      label-width="100px"
    >
      <el-form-item label="店铺" prop="shopId">
        <el-select v-model="formData.shopId" placeholder="请选择店铺" filterable clearable>
          <el-option v-for="item in shopList" :key="item.id" :label="item.name" :value="item.id" />
        </el-select>
      </el-form-item>
      <el-form-item label="员工" prop="employeeId">
        <el-select v-model="formData.employeeId" placeholder="请选择员工" filterable clearable>
          <el-option
            v-for="item in employeeList"
            :key="item.id"
            :label="item.name + ' (' + (item.employeeNo || item.id) + ')'"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="考核周期" prop="period">
        <el-date-picker
          v-model="formData.period"
          class="!w-240px"
          placeholder="请选择考核周期"
          type="month"
          value-format="YYYY-MM"
        />
      </el-form-item>

      <!-- 考核指标(可自定义) -->
      <el-divider content-position="left">考核指标</el-divider>
      <div class="indicator-section">
        <div
          v-for="(indicator, idx) in indicatorList"
          :key="idx"
          class="indicator-row mb-10px"
          style="display: flex; align-items: center; gap: 10px;"
        >
          <el-input
            v-model="indicator.name"
            class="!w-160px"
            placeholder="指标名称"
            @input="onIndicatorChange"
          />
          <span style="flex-shrink: 0;">得分:</span>
          <el-input-number
            v-model="indicator.score"
            :min="0"
            :max="indicator.maxScore || 100"
            :precision="0"
            class="!w-120px"
            @change="onIndicatorChange"
          />
          <span style="flex-shrink: 0;">满分:</span>
          <el-input-number
            v-model="indicator.maxScore"
            :min="1"
            :precision="0"
            class="!w-100px"
            @change="onIndicatorChange"
          />
          <el-button
            :disabled="indicatorList.length <= 1"
            circle
            size="small"
            type="danger"
            @click="removeIndicator(idx)"
          >
            <Icon icon="ep:minus" />
          </el-button>
        </div>
        <el-button size="small" type="primary" plain @click="addIndicator">
          <Icon class="mr-5px" icon="ep:plus" />
          添加指标
        </el-button>
      </div>

      <!-- 自动计算总分和等级 -->
      <el-form-item label="总分">
        <el-input-number
          v-model="formData.score"
          :disabled="true"
          :max="100"
          :min="0"
          :precision="1"
          class="!w-200px"
        />
        <span class="ml-10px text-gray-500">（根据指标自动计算）</span>
      </el-form-item>
      <el-form-item label="等级">
        <el-tag
          v-if="formData.grade"
          :type="gradeTagType(formData.grade)"
          size="large"
          style="font-size: 16px;"
        >
          {{ formData.grade }}
        </el-tag>
        <span v-else class="text-gray-400">请先填写指标</span>
        <span class="ml-10px text-gray-500">（A>=90, B>=80, C>=70, D>=60, E<60）</span>
      </el-form-item>

      <el-form-item label="评语" prop="comment">
        <el-input
          v-model="formData.comment"
          :rows="3"
          placeholder="请输入评语"
          type="textarea"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button :loading="formLoading" type="primary" @click="submitForm">确 定</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>

  <!-- 详情弹窗 -->
  <Dialog v-model="detailVisible" title="考核详情" width="700px">
    <el-descriptions v-if="detailData" :column="2" border>
      <el-descriptions-item label="员工姓名">{{ detailData.employeeName || '-' }}</el-descriptions-item>
      <el-descriptions-item label="岗位">{{ detailData.positionName || '-' }}</el-descriptions-item>
      <el-descriptions-item label="考核周期">{{ detailData.period || '-' }}</el-descriptions-item>
      <el-descriptions-item label="得分">
        <span :style="{ color: getScoreColor(detailData.score), fontWeight: 'bold' }">
          {{ detailData.score != null ? detailData.score : '-' }}
        </span>
      </el-descriptions-item>
      <el-descriptions-item label="等级">
        <el-tag v-if="detailData.grade" :type="gradeTagType(detailData.grade)" size="default">
          {{ detailData.grade }}
        </el-tag>
        <span v-else>-</span>
      </el-descriptions-item>
      <el-descriptions-item label="创建时间">
        {{ detailData.createTime || '-' }}
      </el-descriptions-item>
      <el-descriptions-item label="考核指标" :span="2">
        <div v-if="detailIndicators.length > 0">
          <el-tag
            v-for="(ind, idx) in detailIndicators"
            :key="idx"
            class="mr-5px mb-5px"
            size="small"
          >
            {{ ind.name }}: {{ ind.score }}/{{ ind.maxScore || 100 }}
          </el-tag>
        </div>
        <span v-else>-</span>
      </el-descriptions-item>
      <el-descriptions-item label="评语" :span="2">
        {{ detailData.comment || '-' }}
      </el-descriptions-item>
    </el-descriptions>
  </Dialog>
</template>

<script lang="ts" setup>
import { dateFormatter } from '@/utils/formatTime'
import * as PerformanceApi from '@/api/hotel/performance'
import { getEmployeeListAll } from '@/api/hotel/employee'
import { getShopSimpleList } from '@/api/hotel/platformShop'

defineOptions({ name: 'HotelPerformance' })

const message = useMessage()
const { t } = useI18n()

const loading = ref(true)
const total = ref(0)
const list = ref([])

// 指标项
interface IndicatorItem {
  name: string
  score: number
  maxScore: number
}

// 店铺下拉列表
const shopList = ref<any[]>([])
const getShopList = async () => {
  const data = await getShopSimpleList()
  shopList.value = data || []
}

// 员工下拉列表
const employeeList = ref<any[]>([])
const getEmployeeList = async () => {
  const data = await getEmployeeListAll()
  employeeList.value = data || []
}

const queryParams = reactive<any>({
  pageNo: 1,
  pageSize: 20,
  shopId: undefined,
  employeeId: undefined,
  period: undefined,
  grade: undefined
})
const queryFormRef = ref()

const getList = async () => {
  loading.value = true
  try {
    const data = await PerformanceApi.getPerformancePage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryFormRef.value.resetFields()
  handleQuery()
}

// ========== 弹窗表单 ==========
const dialogVisible = ref(false)
const dialogTitle = ref('')
const formLoading = ref(false)
const formType = ref('')
const indicatorList = ref<IndicatorItem[]>([{ name: '', score: 0, maxScore: 100 }])

const formData = ref<PerformanceApi.PerformanceVO>({
  id: undefined,
  shopId: undefined,
  employeeId: undefined,
  employeeName: undefined,
  positionName: undefined,
  period: '',
  score: 0,
  grade: '',
  metrics: '',
  comment: '',
  appraiserId: undefined,
  appraiseTime: undefined,
  createTime: undefined as any
})
const formRules = reactive({
  shopId: [{ required: true, message: '请选择店铺', trigger: 'change' }],
  employeeId: [{ required: true, message: '请选择员工', trigger: 'change' }],
  period: [{ required: true, message: '请选择考核周期', trigger: 'change' }]
})
const formRef = ref()

// 添加指标
const addIndicator = () => {
  indicatorList.value.push({ name: '', score: 0, maxScore: 100 })
}

// 删除指标
const removeIndicator = (idx: number) => {
  if (indicatorList.value.length <= 1) return
  indicatorList.value.splice(idx, 1)
  onIndicatorChange()
}

// 指标变化时自动计算总分和等级
const onIndicatorChange = () => {
  const indicators = indicatorList.value.filter((i) => i.name && i.maxScore > 0)
  if (indicators.length === 0) {
    formData.value.score = 0
    formData.value.grade = ''
    return
  }

  // 计算加权平均分：每项指标得分/满分*100，然后取平均值
  let totalRatio = 0
  for (const ind of indicators) {
    if (ind.maxScore > 0) {
      totalRatio += (ind.score / ind.maxScore) * 100
    }
  }
  const avgScore = Math.round((totalRatio / indicators.length) * 10) / 10
  formData.value.score = Math.min(avgScore, 100)

  // 自动判定等级
  formData.value.grade = calcGrade(formData.value.score)
}

// 等级判定
const calcGrade = (score: number): string => {
  if (score >= 90) return 'A'
  if (score >= 80) return 'B'
  if (score >= 70) return 'C'
  if (score >= 60) return 'D'
  return 'E'
}

// 等级标签颜色
const gradeTagType = (grade: string): string => {
  const map: Record<string, string> = { A: 'success', B: '', C: 'warning', D: 'danger', E: 'info' }
  return map[grade] || ''
}

// 得分颜色
const getScoreColor = (score: number): string => {
  if (score == null) return '#999'
  if (score >= 90) return '#67c23a'
  if (score >= 80) return '#409eff'
  if (score >= 70) return '#e6a23c'
  if (score >= 60) return '#f56c6c'
  return '#909399'
}

// 解析指标JSON
const getIndicators = (metrics: string): IndicatorItem[] => {
  if (!metrics) return []
  try {
    return JSON.parse(metrics)
  } catch {
    return []
  }
}

// 打开弹窗
const openForm = async (type: string, id?: string | number) => {
  dialogVisible.value = true
  dialogTitle.value = type === 'create' ? '新增考核' : '修改考核'
  formType.value = type
  resetForm()
  if (id) {
    formLoading.value = true
    try {
      const data = await PerformanceApi.getPerformance(id)
      formData.value = data
      // 解析指标
      const metrics = data.metrics
      if (metrics) {
        try {
          const parsed = JSON.parse(metrics)
          if (Array.isArray(parsed) && parsed.length > 0) {
            indicatorList.value = parsed
          } else {
            indicatorList.value = [{ name: '', score: 0, maxScore: 100 }]
          }
        } catch {
          indicatorList.value = [{ name: '', score: 0, maxScore: 100 }]
        }
      } else {
        indicatorList.value = [{ name: '', score: 0, maxScore: 100 }]
      }
      onIndicatorChange()
    } finally {
      formLoading.value = false
    }
  }
}

// 提交表单
const submitForm = async () => {
  if (!formRef) return
  const valid = await formRef.value.validate()
  if (!valid) return

  // 校验至少有一个有效指标
  const validIndicators = indicatorList.value.filter((i) => i.name.trim())
  if (validIndicators.length === 0) {
    message.warning('请至少添加一个考核指标')
    return
  }

  formLoading.value = true
  try {
    const data = formData.value
    // 将指标序列化为JSON
    data.metrics = JSON.stringify(
      indicatorList.value.filter((i) => i.name.trim())
    )
    // 重新计算总分和等级
    onIndicatorChange()

    if (formType.value === 'create') {
      await PerformanceApi.createPerformance(data)
      message.success(t('common.createSuccess'))
    } else {
      await PerformanceApi.updatePerformance(data)
      message.success(t('common.updateSuccess'))
    }
    dialogVisible.value = false
    await getList()
  } finally {
    formLoading.value = false
  }
}

// 重置表单
const resetForm = () => {
  formData.value = {
    id: undefined,
    shopId: undefined,
    employeeId: undefined,
    employeeName: undefined,
    positionName: undefined,
    period: '',
    score: 0,
    grade: '',
    metrics: '',
    comment: '',
    appraiserId: undefined,
    appraiseTime: undefined,
    createTime: undefined as any
  }
  indicatorList.value = [{ name: '', score: 0, maxScore: 100 }]
  formRef.value?.resetFields()
}

// 删除
const handleDelete = async (id: string | number) => {
  try {
    await message.delConfirm()
    await PerformanceApi.deletePerformance(id)
    message.success(t('common.delSuccess'))
    await getList()
  } catch {}
}

// ========== 详情弹窗 ==========
const detailVisible = ref(false)
const detailData = ref<any>(null)
const detailIndicators = ref<IndicatorItem[]>([])

const openDetail = async (row: any) => {
  detailVisible.value = true
  formLoading.value = true
  try {
    const data = await PerformanceApi.getPerformance(row.id)
    detailData.value = data
    detailIndicators.value = getIndicators(data.metrics)
  } finally {
    formLoading.value = false
  }
}

onMounted(() => {
  getList()
  getShopList()
  getEmployeeList()
})
</script>

<style scoped>
.indicator-section {
  padding: 10px 0;
}
.indicator-row {
  flex-wrap: wrap;
}
</style>
